import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { getQueryParamsObjectToString } from 'utils/helpers/router'
import { toastSuccess } from 'utils/toast'
import {
  getVendors,
  getVendorRequest,
  postSendVendorEmail,
  putVendorActiveStatus,
  type GetVendorListResponseInterface,
  type SendVendorEmailRequestInterface,
} from 'features/Enterprise/Vendor/services/vendorList'
import { getVendorDetails } from 'features/Enterprise/Vendor/services/addEditVendor'
import { getVendorDetailRequest } from 'features/Enterprise/Vendor/services/vendorReview'
import { PRINCIPAL_VENDOR } from 'features/Enterprise/Vendor/@types/vendorTypes'

import {
  sliceName,
  setLoading,
  setVendorList,
  setVendorRequestList,
  setVendorRequestCount,
  setVendorActiveStatus,
  setPrincipalCount,
  setVendorCount,
  initialQuery,
} from './vendorListSlice'

export const fetchVendors = createAsyncThunk(
  `${sliceName}/fetchVendors`,
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorList: { query },
      } = getState() as StoreStateType
      const parseQuery = getQueryParamsObjectToString(query)
      const { data } = await getVendors(parseQuery)
      dispatch(setVendorList(data as unknown as GetVendorListResponseInterface))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchVendorRequests = createAsyncThunk(
  `${sliceName}/fetchVendorRequests`,
  async (type: string, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true))
      const {
        vendorList: { query },
      } = getState() as StoreStateType
      const statusType = type !== 'DRAFT' ? query.status : type
      const newQuery = { ...query, status: statusType }
      const { data } = await getVendorRequest(newQuery)
      await dispatch(setVendorRequestList(data.data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchDraftVendorRequestsCount = createAsyncThunk(
  `${sliceName}/fetchDraftVendorRequestsCount`,
  async (status: string, { dispatch }) => {
    try {
      const { data } = await getVendorRequest({ status })
      const totalElements = data?.data?.pagination?.total_elements || 0
      dispatch(setVendorRequestCount({ status, count: totalElements }))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchVendorTotal = createAsyncThunk(
  `${sliceName}/fetchVendorTotal`,
  async (status: string, { dispatch }) => {
    try {
      const newQuery = { ...initialQuery, source_type: status }
      const parseQuery = getQueryParamsObjectToString(newQuery)
      const data = await getVendors(parseQuery)
      const dataResponse = data.data as unknown as GetVendorListResponseInterface

      const totalElements = dataResponse.pagination.total_elements || 0
      if (status === PRINCIPAL_VENDOR) {
        dispatch(setPrincipalCount(totalElements))
      } else {
        dispatch(setVendorCount(totalElements))
      }
      return dataResponse.pagination.total_elements || 0
    } catch (error) {
      callErrorMsg(error)
      return 0
    }
  },
)

export const submitUpdateVendorPortal = createAsyncThunk(
  `${sliceName}/submitUpdateVendorPortal`,
  async (
    { payload, cb }: { payload: SendVendorEmailRequestInterface; cb: () => void },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      await postSendVendorEmail(payload)
      let toastMessage = 'Berhasil kirim email konfirmasi!'
      if (payload.status === 'Suspended') toastMessage = 'User berhasil di Suspend!'

      toastSuccess(toastMessage)
      cb()
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const updateVendorActiveStatus = createAsyncThunk(
  `${sliceName}/updateVendorActiveStatus`,
  async ({ vendor_id, active }: { vendor_id: number; active: boolean }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      await putVendorActiveStatus(vendor_id, active)
      toastSuccess(`Vendor berhasil ${active ? 'diaktifkan' : 'dinonaktifkan'}`)
      dispatch(setVendorActiveStatus({ vendor_id, active }))
    } catch (error) {
      throw callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const fetchVendorDetailsOnList = createAsyncThunk(
  `${sliceName}/fetchVendorDetailsOnList`,
  async (vendor_id: string, { rejectWithValue, dispatch }) => {
    dispatch(setLoading(true))
    try {
      const { data } = await getVendorDetails(vendor_id)
      const relationships = data.data.relationships || {}
      dispatch(setLoading(false))

      return relationships
    } catch (error) {
      callErrorMsg(error)
      dispatch(setLoading(false))

      return rejectWithValue(error)
    }
  },
)

export const fetchVendorDetailRequestOnList = createAsyncThunk(
  `${sliceName}/fetchVendorDetailRequestOnList`,
  async ({ vendorId }: { vendorId: string }, { dispatch }) => {
    try {
      dispatch(setLoading(true))
      const { data } = await getVendorDetailRequest(vendorId)
      const requestData = data.data.request_data || {}
      return requestData.source_type || undefined
    } catch (error) {
      callErrorMsg(error)
      return false
    } finally {
      dispatch(setLoading(false))
    }
  },
)
